import { useState } from "react";

interface PreviewPanelProps {
  content: string;
  agentType: string;
}

function renderPreviewMarkdown(text: string): JSX.Element[] {
  const lines = text.split("\n");
  const elements: JSX.Element[] = [];
  let i = 0;
  let tableBuffer: string[] = [];

  const flushTable = (buf: string[], key: string) => {
    if (buf.length < 2) return null;
    const headerRow = buf[0].split("|").filter((c) => c.trim() !== "");
    const dataRows = buf.slice(2).map((r) => r.split("|").filter((c) => c.trim() !== ""));
    return (
      <table key={key} className="w-full text-[11px] border-collapse my-2">
        <thead>
          <tr className="bg-[#F7F8F8]">
            {headerRow.map((h, hi) => (
              <th key={hi} className="border border-[#E5E6EB] px-2 py-1 text-left text-[#1D2129] font-medium">{h.trim()}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => (
            <tr key={ri} className="hover:bg-[#F7F8F8]">
              {row.map((cell, ci) => (
                <td key={ci} className="border border-[#E5E6EB] px-2 py-1 text-[#4E5969]">{cell.trim()}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("|")) {
      tableBuffer.push(line);
      i++;
      continue;
    } else if (tableBuffer.length > 0) {
      const tbl = flushTable(tableBuffer, `table-${i}`);
      if (tbl) elements.push(tbl);
      tableBuffer = [];
    }

    if (line.startsWith("## ")) {
      elements.push(
        <h2 key={i} className="text-[14px] font-semibold text-[#1D2129] mt-5 mb-2 first:mt-0">
          {line.replace("## ", "")}
        </h2>
      );
    } else if (line.startsWith("### ")) {
      elements.push(
        <h3 key={i} className="text-[12px] font-semibold text-[#1D2129] mt-3 mb-1.5">
          {line.replace("### ", "")}
        </h3>
      );
    } else if (line.startsWith("---")) {
      elements.push(<hr key={i} className="border-[#E5E6EB] my-4" />);
    } else if (line.match(/^\d+\. \*\*/)) {
      const match = line.match(/^(\d+)\. \*\*(.+?)\*\*[：:](.*)/);
      if (match) {
        elements.push(
          <div key={i} className="flex gap-2 text-[12px] leading-[1.7] mb-1.5">
            <span className="text-[#3D69E8] font-semibold flex-shrink-0">{match[1]}.</span>
            <span>
              <strong className="font-medium text-[#1D2129]">{match[2]}</strong>
              <span className="text-[#4E5969]">{match[3]}</span>
            </span>
          </div>
        );
      }
    } else if (line.startsWith("   - ")) {
      elements.push(
        <div key={i} className="flex gap-1.5 text-[12px] text-[#4E5969] leading-[1.6] mb-1 pl-6">
          <span className="text-[#C9CDD4] flex-shrink-0">–</span>
          <span
            dangerouslySetInnerHTML={{
              __html: line.replace("   - ", "").replace(/\*\*(.+?)\*\*/g, '<strong class="font-medium text-[#1D2129]">$1</strong>')
            }}
          />
        </div>
      );
    } else if (line.startsWith("- **")) {
      const match = line.match(/- \*\*(.+?)\*\*[：:（(](.*)/);
      if (match) {
        elements.push(
          <div key={i} className="flex gap-1.5 text-[12px] leading-[1.7] mb-1.5 pl-2">
            <span className="text-[#646479] flex-shrink-0">•</span>
            <span>
              <strong className="font-medium text-[#1D2129]">{match[1]}</strong>
              <span className="text-[#4E5969]">{match[2].replace(/[）)]$/, "")}</span>
            </span>
          </div>
        );
      }
    } else if (line.startsWith("- ")) {
      elements.push(
        <div key={i} className="flex gap-1.5 text-[12px] text-[#4E5969] leading-[1.6] mb-1.5 pl-2">
          <span className="text-[#646479] flex-shrink-0">•</span>
          <span dangerouslySetInnerHTML={{
            __html: line.replace("- ", "").replace(/\*\*(.+?)\*\*/g, '<strong class="font-medium text-[#1D2129]">$1</strong>')
          }} />
        </div>
      );
    } else if (line.trim() !== "") {
      elements.push(
        <p
          key={i}
          className="text-[12px] text-[#4E5969] leading-[1.8] mb-2"
          dangerouslySetInnerHTML={{
            __html: line
              .replace(/\*\*(.+?)\*\*/g, '<strong class="font-medium text-[#1D2129]">$1</strong>')
              .replace(/（(.+?)）/g, '<span class="text-[#646479]">（$1）</span>')
          }}
        />
      );
    }
    i++;
  }

  if (tableBuffer.length > 0) {
    const tbl = flushTable(tableBuffer, "table-end");
    if (tbl) elements.push(tbl);
  }

  return elements;
}

export default function PreviewPanel({ content, agentType }: PreviewPanelProps) {
  const [inputValue, setInputValue] = useState("");
  const [viewMode, setViewMode] = useState<"preview" | "code">("preview");

  return (
    <div className="flex flex-col h-full bg-[#FAFAFA]">
      <div className="flex items-center justify-between px-4 h-11 border-b border-[#E5E6EB] flex-shrink-0 bg-white">
        <span className="text-[13px] font-semibold text-[#1D2129]">预览与调试</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setViewMode("preview")} className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${viewMode === "preview" ? "text-[#3D69E8] bg-[#3D69E8]/10" : "text-[#646479] hover:bg-[#F5F6F9]"}`}><i className="ri-layout-5-line text-[13px]"></i></button>
          <button onClick={() => setViewMode("code")} className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${viewMode === "code" ? "text-[#3D69E8] bg-[#3D69E8]/10" : "text-[#646479] hover:bg-[#F5F6F9]"}`}><i className="ri-code-line text-[13px]"></i></button>
          <button className="w-7 h-7 flex items-center justify-center text-[#646479] hover:bg-[#F5F6F9] rounded-lg transition-colors cursor-pointer"><i className="ri-refresh-line text-[13px]"></i></button>
          <button className="w-7 h-7 flex items-center justify-center text-[#646479] hover:bg-[#F5F6F9] rounded-lg transition-colors cursor-pointer"><i className="ri-fullscreen-line text-[13px]"></i></button>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-[#E5E6EB] bg-white flex-shrink-0">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E6EB] bg-[#F9FAFB] px-2.5 py-1">
          <div className="w-1.5 h-1.5 rounded-full bg-[#00B42A]"></div>
          <span className="text-[11px] text-[#4E5969]">{agentType} · 运行中</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="rounded-2xl border border-[#E5E6EB] bg-white p-4 shadow-[0_1px_0_rgba(15,23,42,0.02)]">
          <div className="text-[11px] font-medium text-[#8B8FA8] mb-2">预览内容</div>
          <div className="space-y-1.5">{renderPreviewMarkdown(content)}</div>
        </div>
      </div>

      <div className="border-t border-[#E5E6EB] bg-white px-4 py-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <div className="flex-1 min-h-[42px] max-h-[140px] border border-[#E5E6EB] rounded-xl px-3 py-2.5 flex items-end gap-2 focus-within:border-[#3D69E8] focus-within:ring-2 focus-within:ring-[#3D69E8]/10 transition-all bg-white">
            <div className="flex items-center gap-1 flex-shrink-0">
              <button className="w-5 h-5 flex items-center justify-center text-[#C9CDD4] hover:text-[#646479] cursor-pointer transition-colors"><i className="ri-attachment-line text-sm"></i></button>
              <button className="w-5 h-5 flex items-center justify-center text-[#C9CDD4] hover:text-[#646479] cursor-pointer transition-colors"><i className="ri-image-line text-sm"></i></button>
              <button className="w-5 h-5 flex items-center justify-center text-[#C9CDD4] hover:text-[#646479] cursor-pointer transition-colors"><i className="ri-emotion-line text-sm"></i></button>
            </div>
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="输入消息，Enter 发送，Shift + Enter 换行"
              rows={1}
              className="flex-1 text-[12px] text-[#1D2129] bg-transparent outline-none resize-none leading-[1.6] placeholder:text-[#C9CDD4]"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); setInputValue(""); } }}
            />
          </div>
          <button className="w-10 h-10 flex items-center justify-center bg-[#3D69E8] text-white rounded-xl hover:bg-[#2d57d1] transition-colors cursor-pointer flex-shrink-0 shadow-sm"><i className="ri-arrow-up-line text-sm"></i></button>
        </div>
      </div>
    </div>
  );
}
